'use client';

import { Button } from '@common/components';
import { usePathname } from 'next/navigation';
import { type PropsWithChildren, useEffect, useState } from 'react';

import { MEMBER_JOIN_STATUS } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { ROUTES, STORAGE_KEY, SUB_DOMAIN } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';

const RecommendPolicyTemplate = ({ children }: PropsWithChildren) => {
  const pathname = usePathname();
  //* SSR·하이드레이션 시점엔 닫힘으로 시작한다(sessionStorage를 렌더 중 읽으면 마크업이 갈린다).
  const [isDismissed, setIsDismissed] = useState(true);
  const isEmptyFilter = useAuthStore(
    (state) =>
      state.myInfo?.memberJoinStatus === MEMBER_JOIN_STATUS.EMPTY_FILTER,
  );

  //* KB(KB Pay)는 정책서비스가 메인이라 코드입력이 필수이며, 진입 지점(cross-domain/page,
  //* kb-join/common/layout)이 EMPTY_FILTER를 KB_PAY_FILTER로 강제한다. 따라서 웹 전용
  //* "나중에/받을래요" 선택 다이얼로그는 KB에 노출하지 않는다.
  const isKbPay = useDeviceInfoStore(
    (state) => state.subDomain === SUB_DOMAIN.KB_PAY,
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sessionStorage 기반 마운트 init (SSR 하이드레이션 게이트)
    setIsDismissed(!!sessionStorage.getItem(STORAGE_KEY.POLICY_FILTER_LANDING));
  }, []);

  const isOpened = !isDismissed && isEmptyFilter && !isKbPay;

  return (
    <>
      {children}
      <ResponsiveDialog
        opened={isOpened}
        onClose={() => setIsDismissed(true)}
        drawerType="bottom"
      >
        <div className="pb-16">
          <h3 className="mb-8 font-700 text-Gray-900 typo-body2">
            나에게 맞는 정책을 받아보세요!
          </h3>
          <p className="font-400 text-Gray-700 typo-body4">
            관심사를 알려주시면, 딱 맞는 정책을 추천해 드릴게요.
          </p>
          <footer className="mt-28 flex gap-8">
            <Button
              className="min-w-120"
              theme="primary-line"
              onClick={() => {
                setIsDismissed(true);
                sessionStorage.setItem(
                  STORAGE_KEY.POLICY_FILTER_LANDING,
                  'ignore',
                );
              }}
            >
              나중에
            </Button>
            <Button asChild className="flex-1">
              <CustomLink
                href={ROUTES.POLICY_FILTER.pathname({
                  situation: 'join',
                })}
                onClick={() =>
                  sessionStorage.setItem(
                    STORAGE_KEY.POLICY_FILTER_LANDING_FROM,
                    pathname ?? '',
                  )
                }
              >
                네, 받을래요
              </CustomLink>
            </Button>
          </footer>
        </div>
      </ResponsiveDialog>
    </>
  );
};

export default RecommendPolicyTemplate;
